import React, { useEffect } from "react";
import { getSfxBus } from "../utils/audioBus";
import { setAudioSettings } from "../utils/audioSettings";
import { playUiSound } from "../utils/sfx";
import { useAudioSettings } from "./useAudioSettings";

/**
 * The audio controls (master / SFX / music volume + mute), backed by the
 * persisted store in `utils/audioSettings`. A section rather than a screen
 * of its own: settings live inside the pause menu, which is the only place
 * the game stops.
 */
export const AudioSettingsSection: React.FC = () => {
  const settings = useAudioSettings();

  // Build the audio graph up front (if a sound hasn't already) so dragging a
  // slider is audible immediately, not only after the first release tick.
  useEffect(() => {
    getSfxBus();
  }, []);

  return (
    <section className="flex flex-col gap-4">
      <h3 className="font-condensed font-semibold text-sm uppercase tracking-[0.15em] text-ink-fade">
        Audio
      </h3>

      <VolumeSlider
        id="settings-volume-master"
        label="Master"
        value={settings.master}
        muted={settings.muted}
        onChange={(master) => setAudioSettings({ master })}
      />
      <VolumeSlider
        id="settings-volume-sfx"
        label="Sound Effects"
        value={settings.sfx}
        muted={settings.muted}
        onChange={(sfx) => setAudioSettings({ sfx })}
      />
      <VolumeSlider
        id="settings-volume-music"
        label="Music"
        value={settings.music}
        muted={settings.muted}
        onChange={(music) => setAudioSettings({ music })}
      />

      <label className="flex flex-row items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={settings.muted}
          className="w-4 h-4 accent-gold-dark"
          onChange={(e) => {
            setAudioSettings({ muted: e.target.checked });
            // Confirm the toggle audibly (silent, of course, when muting).
            playUiSound("ui-click");
          }}
        />
        <span className="font-condensed uppercase tracking-wider text-sm text-ink-black">
          Mute all
        </span>
      </label>
    </section>
  );
};

interface VolumeSliderProps {
  id: string;
  label: string;
  /** 0..1 */
  value: number;
  muted: boolean;
  onChange: (value: number) => void;
}

const VolumeSlider: React.FC<VolumeSliderProps> = ({
  id,
  label,
  value,
  muted,
  onChange,
}) => {
  const percent = Math.round(value * 100);
  // A soft tick on release so the player hears the level they've landed on.
  const tick = () => playUiSound("ui-click");
  return (
    <div
      className={
        muted ? "flex flex-col gap-1 opacity-50" : "flex flex-col gap-1"
      }
    >
      <div className="flex items-baseline justify-between">
        <label
          htmlFor={id}
          className="font-condensed uppercase tracking-wider text-sm text-ink-black"
        >
          {label}
        </label>
        <span className="text-xs text-ink-fade tabular-nums">{percent}%</span>
      </div>
      <input
        id={id}
        type="range"
        min={0}
        max={100}
        value={percent}
        className="w-full accent-gold-dark"
        onChange={(e) => onChange(Number(e.target.value) / 100)}
        onPointerUp={tick}
        onKeyUp={tick}
      />
    </div>
  );
};
